import { useEffect, useState } from 'react';
// import DataTableButton from '@components/buttons/dataTableTopButton';
import { useTheme, Avatar, Chip, IconButton, Menu, MenuItem, Box, Select, SelectChangeEvent, Checkbox, ListItemText } from '@mui/material';
import { DataGrid, GridColDef, GridFilterInputValue, GridMoreVertIcon, GridRenderEditCellParams, useGridApiContext, useGridApiRef } from '@mui/x-data-grid';
import { Operation, OperationAgencyOfficial, OperationCheckInState, OperationTeam, createOperationCheckIn, updateOperationOfficials } from '../../../../../networkRequest/operationsService';
import StyledToolBar from '@components/StyledToolBar';
import Utils from '../../../../../utils/utils';
import { useUser } from '@hooks/useUser';

const TeamMembershipEditCell = (props) => {
  const teams = props.teams;
  const teamMembers = props.value;
  const team_ids = teamMembers.map((m) => m.operation_team_id);
  const apiRef = useGridApiContext();

  return (
    <Select
      sx={{ width: '100%', fontSize: '14px' }}
      labelId="team-multi-select"
      label="Team"
      multiple
      value={team_ids ?? []}
      onClose={() => {
        apiRef.current.stopCellEditMode({ id: props.id, field: props.field });
      }}
      onChange={(e) => {
        const newTeamIds = e.target.value;

        // Calculate the new team members array
        const teamMembersByTeamId = {};
        teamMembers.map((tm) => {
          teamMembersByTeamId[tm.operation_team_id] = tm;
        });
        const newTeamMembers = newTeamIds.map((newTeamId) => {
          const existingTeamMember = teamMembersByTeamId[newTeamId];
          if (existingTeamMember !== undefined) {
            return existingTeamMember;
          }
          return { operation_team_id: newTeamId };
        });

        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newTeamMembers });
      }}
      renderValue={(selected) =>
        teams
          .filter((t) => selected.includes(t.id))
          .map((t) => t.name)
          .join(', ')
      }
    >
      {teams.length ? (
        teams.map((team) => (
          <MenuItem key={team.id} value={team.id}>
            <Checkbox checked={team_ids.includes(team.id)} />
            <ListItemText primary={team.name} />
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>First add a team</MenuItem>
      )}
    </Select>
  );
};

const STATUS_OPTIONS = [
  { value: 'checked_in', label: 'Checked In' },
  { value: 'checked_out', label: 'Checked Out' },
  { value: 'added', label: 'Added' },
];

function StatusEditCell(props: GridRenderEditCellParams) {
  const apiRef = useGridApiContext();
  const currentStatus = props.value;

  const handleChange = async (event: SelectChangeEvent) => {
    const newStatus = event.target.value;
    apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newStatus }, event);
    apiRef.current.stopCellEditMode({ id: props.id, field: props.field });
  };

  return (
    <Select
      autoFocus
      size="small"
      fullWidth
      value={currentStatus}
      onChange={handleChange}
      displayEmpty
      renderValue={(value: OperationCheckInState) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckInStatusChip state={value} />
          </Box>
        );
      }}
    >
      {STATUS_OPTIONS
        .filter(option => option.value !== 'added')
        .map(option => (
          <MenuItem key={option.value} value={option.value}>
            <CheckInStatusChip state={option.value as OperationCheckInState} />
          </MenuItem>
        ))}
    </Select>
  );
}

const CheckInStatusChip = ({ state }: { state: OperationCheckInState }) => {
  const theme = useTheme();

  const STATUS_RENDER_ATTRIBUTES = {
    checked_in: {
      text: 'Checked In',
      color: theme.palette.green[50],
    },
    checked_out: {
      text: 'Checked Out',
      color: theme.palette.orange[50],
    },
    added: {
      text: 'Added',
      color: theme.palette.action.selected
    },
  };

  const status_render_attributes = STATUS_RENDER_ATTRIBUTES[state];

  return <>
    <Chip
      label={status_render_attributes.text}
      variant='filled'
      sx={{ backgroundColor: status_render_attributes.color, px: '14px', fontSize: '14px' }}
    />
  </>
}

interface ParticipantsTableProps {
  participants: OperationAgencyOfficial[];
  teams: OperationTeam[]
  onOperationUpdate: (operation: Operation) => void
}

const ParticipantsTable = ({ participants, teams, onOperationUpdate }: ParticipantsTableProps) => {
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [participantToRemove, setParticipantToRemove] = useState<OperationAgencyOfficial | null>(null);
  const [rows, setRows] = useState<OperationAgencyOfficial[]>(participants);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setParticipantToRemove(null);
    setAnchorEl(null);
  }
  const handleOpen = (params: GridRenderEditCellParams<OperationAgencyOfficial, unknown>) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setParticipantToRemove(params.row);
      setAnchorEl(event.currentTarget);
    };
  // this could be used to set a loading indicator (not currently implemented)
  const [_savingRowId, setSavingRowId] = useState<string | null>(null);
  const gridApiRef = useGridApiRef();

  const teamNamesById = teams.reduce((acc, team) => {
    acc[team.id] = team.name;
    return acc;
  }, {});

  useEffect(() => {
    setRows(participants);
  }, [participants])

  const removeParticipant = async () => {
    if (participantToRemove && user) {
      const response = await updateOperationOfficials({
        accessToken: user.accessToken,
        operationId: participantToRemove.operation_id,
        operationOfficialData: [
          {
            agency_official_id: participantToRemove.agency_official.id,
            active: false
          }
        ]
      })
      if (response.data) {
        setRows(prev =>
          prev.filter(participant => participant.id !== participantToRemove.id)
        );
        onOperationUpdate(response.data);
        handleClose();
      } else {
        throw new Error(response.error)
      }
    }
  }

  const handleCellClick = (params, event) => {
    // start editing when single clicking on editable cells
    if (!params.isEditable || !gridApiRef.current) return;

    const mode = gridApiRef.current.getCellMode?.(params.id, params.field)

    if (mode !== 'view') return;

    if (params.colDef.editable) {
      event.defaultMuiPrevented = true;
      gridApiRef.current.startCellEditMode({ id: params.id, field: params.field });
    }
  };

  const handleProcessRowUpdate = async (newRow: OperationAgencyOfficial, oldRow: OperationAgencyOfficial) => {
    try {
      setSavingRowId(newRow.id);
      if (!user) {
        throw new Error('User not found');
      }

      // Update assignment
      if (newRow.assignment !== oldRow.assignment) {
        const response = await updateOperationOfficials({
          accessToken: user.accessToken,
          operationId: newRow.operation_id,
          operationOfficialData: [
            {
              agency_official_id: newRow.agency_official.id,
              assignment: newRow.assignment
            }
          ]
        });
        if (response.error || response.data === undefined) {
          return oldRow;
        }
        onOperationUpdate(response.data);
        return newRow;
      }

      // Update check-in state
      if (newRow.operation_check_in?.state !== oldRow.operation_check_in?.state) {
        const response = await createOperationCheckIn(user.accessToken, newRow.operation_id, {
          operation_agency_official_id: newRow.id,
          state: newRow.operation_check_in.state
        });
        if (response.error) {
          // Should handle the error in some way probably by displaying a toast
          return oldRow;
        }
        return newRow;
      }

      // Update team membership
      const oldTeamIds = new Set(oldRow.operation_team_members.map((tm) => tm.operation_team_id));
      const newTeamIds = new Set(newRow.operation_team_members.map((tm) => tm.operation_team_id));
      if (!Utils.areSetsEqual(oldTeamIds, newTeamIds)) {
        const response = await updateOperationOfficials({
          accessToken: user.accessToken,
          operationId: newRow.operation_id,
          operationOfficialData: [
            {
              agency_official_id: newRow.agency_official.id,
              team_assignments: newRow.operation_team_members.map((tm) => ({ team_id: tm.operation_team_id })),
              // Assignment will be erased if this is not included
              assignment: newRow.assignment
            }
          ]
        });

        if (response.error || response.data === undefined) {
          return oldRow;
        }
        onOperationUpdate(response.data);
        return newRow;
      }

      // If the edit was cancelled, return the old row
      return oldRow;
    }
    catch (error) {
      console.log('handleProcessRowUpdate error', error);
      return oldRow;
    }
    finally {
      setSavingRowId(null);
    }
  };

  const getTeamNamesString = (operationTeamMembers) => {
    return operationTeamMembers.map(m => teamNamesById[m.operation_team_id]).join(', ');
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      editable: false,
      valueGetter: (_, row) => {
        // Construct the full name for search/filter/sort
        return Utils.fullName(row.agency_official.first_name, row.agency_official.last_name);
      },
      renderCell: (params: { row: OperationAgencyOfficial }) => {
        return (
          <>
            <span style={{ display: 'flex', alignItems: 'center' }}><Avatar src={params.row.agency_official.img_url} sx={{ width: 32, height: 32, marginRight: '8px' }} />
              {Utils.fullName(params.row.agency_official.first_name, params.row.agency_official.last_name)}
            </span>
          </>
        );
      }
    },
    {
      field: 'agency',
      headerName: 'Agency',
      flex: 1,
      editable: false,
      valueGetter: (_, row) => {
        return row.agency_official?.agency?.name ?? ''
      },
      // TODO: ensure agency name is returned for Operation Agency Officials
      renderCell: (params: { row: OperationAgencyOfficial }) => params.row.agency_official?.agency?.name ?? ''
    },
    {
      field: 'team',
      headerName: 'Team',
      flex: 1,
      editable: true,
      valueGetter: (_, row) => {
        return row.operation_team_members;
      },
      valueSetter: (value, row) => {
        return {
          ...row,
          operation_team_members: value
        };
      },
      renderCell: (params: { row: OperationAgencyOfficial }) => {
        return getTeamNamesString(params.row.operation_team_members);
      },
      renderEditCell: (params) => {
        return (
          <TeamMembershipEditCell {...params} teams={teams} />
        );
      },
      sortComparator: (otm1, otm2) => {
        return getTeamNamesString(otm1).localeCompare(getTeamNamesString(otm2));
      },
      // Define a custom contains filter that returns rows whose concatenated
      // team names match the filter text
      filterOperators: [
        {
          label: 'Contains',
          value: 'contains',
          InputComponent: GridFilterInputValue,
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value || !filterItem.operator) {
              return null;
            }
            return (value) => {
              const lowerCaseDisplayValue = getTeamNamesString(value).toLowerCase();
              const lowerCaseFilterValue = filterItem.value.toLowerCase();
              return (lowerCaseDisplayValue.indexOf(lowerCaseFilterValue) !== -1);
            };
          }
        }
      ]
    },
    {
      field: 'assignment',
      headerName: 'Assignment',
      flex: 1,
      editable: true,
      valueGetter: (_, row) => {
        return row.assignment;
      },
      valueSetter: (value, row) => {
        const assignment = (value !== null && value.length > 0) ? value : null;
        return {
          ...row,
          assignment: assignment
        };
      },
      renderCell: (params: { row: OperationAgencyOfficial }) => {
        return params.row.assignment ?? 'None';
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      flex: 1,
      editable: true,
      valueOptions: STATUS_OPTIONS,
      renderCell: (params: { row: OperationAgencyOfficial }) => {
        return <>
          <CheckInStatusChip
            state={params.row.operation_check_in?.state ?? "added"}
          />
        </>
      },
      renderEditCell: (params) => <StatusEditCell {...params} />,
      valueGetter: (_, row) => row.operation_check_in?.state ?? "added",
      valueSetter: (value, row) => ({
        ...row,
        operation_check_in: {
          ...(row.operation_check_in ?? {}),
          state: value
        },
      }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={handleOpen(params)}
            aria-controls={open ? 'row-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <GridMoreVertIcon />
          </IconButton>

          <Menu
            id="row-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={removeParticipant}
            >
              Remove
            </MenuItem>
          </Menu>
        </>
      )
    },
  ];

  const Toolbar = () => (
    <StyledToolBar>
      <Box>
        {/* <DataTableButton
          background={alpha(theme.palette.background.paper, 0.7)}
          buttonText="Edit team"
          onClick={() => console.log('Edit team')}
        />
        <DataTableButton
          background={alpha(theme.palette.background.paper, 0.7)}
          buttonText="Edit Assignment"
          onClick={() => console.log('Edit assignment')}
        />
        <DataTableButton
          background={alpha(theme.palette.background.paper, 0.7)}
          buttonText="Edit Status"
          onClick={() => console.log('Edit status')}
        />
        <DataTableButton
          background={alpha(theme.palette.background.paper, 0.7)}
          buttonText="Remove"
          onClick={() => console.log('Remove participants')}
        /> */}
      </Box>
    </StyledToolBar>
  );

  return (
    <DataGrid
      columns={columns}
      rows={rows}
      processRowUpdate={handleProcessRowUpdate}
      onCellClick={handleCellClick}
      checkboxSelection
      disableColumnSelector={true}
      disableRowSelectionOnClick={true}
      showToolbar
      slots={{
        toolbar: () => <Toolbar />
      }}
      apiRef={gridApiRef}
      editMode='cell'
    />
  );
};

export default ParticipantsTable;